import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { Contact } from '@neuroedge/shared';
import { Button, Modal, Select, TextField, useToast } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useContacts } from '../../api/contacts';
import { useBookableServices, useCreateAppointment, useServiceSlots } from '../../api/appointments';
import { QuickContactModal } from '../contacts/QuickContactModal';

/** The "time" select's value for "pick another time" (outside the open slots). */
const OTHER_TIME = '__other__';

/**
 * Staff books an appointment by hand (e.g. over the phone): contact (or a new
 * one on the spot), a bookable service, one of its open slots or another
 * future time, notes, and an optional WhatsApp confirmation.
 */
export function NewAppointmentModal({
  initialDate,
  onClose,
}: {
  /** The day selected on the calendar - pre-fills "another time". */
  initialDate: Date;
  onClose: () => void;
}) {
  const { hasPermission } = useAuth();
  const contacts = useContacts({ pageSize: 500, sortBy: 'displayName', sortOrder: 'asc' });
  const services = useBookableServices();
  const create = useCreateAppointment();
  const toast = useToast();

  const [contactId, setContactId] = useState('');
  const [newContact, setNewContact] = useState<Contact | null>(null);
  const [addingContact, setAddingContact] = useState(false);
  const [serviceId, setServiceId] = useState('');
  const [slot, setSlot] = useState('');
  const [otherTime, setOtherTime] = useState(() => {
    const d = new Date(initialDate);
    d.setHours(10, 0, 0, 0);
    return toLocalInput(d);
  });
  const [notes, setNotes] = useState('');
  const [notify, setNotify] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const slots = useServiceSlots(serviceId);

  const clear = (key: string) => setErrors(({ [key]: _gone, ...rest }) => rest);

  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!contactId) e.contactId = 'Choose a contact.';
    if (!serviceId) e.productServiceId = 'Choose a service.';
    if (!slot) e.scheduledStart = 'Choose a time.';
    else if (slot === OTHER_TIME) {
      const t = new Date(otherTime);
      if (!otherTime || Number.isNaN(t.getTime())) e.scheduledStart = 'Enter a date and time.';
      else if (t.getTime() <= Date.now()) e.scheduledStart = 'Choose a time in the future.';
    }
    if (notes.trim().length > 1000) e.notes = 'Notes must be 1000 characters or fewer.';
    return e;
  }

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setError('Please fix the highlighted fields.');
      return;
    }
    try {
      await create.mutateAsync({
        productServiceId: serviceId,
        contactId,
        scheduledStart: slot === OTHER_TIME ? new Date(otherTime).toISOString() : slot,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        notify,
      });
      toast.success('Appointment booked.');
      onClose();
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const server = fieldErrorsFromApiError(err);
        if (Object.keys(server).length > 0) {
          setErrors(server);
          setError('Please fix the highlighted fields.');
          return;
        }
      }
      setError(err instanceof Error ? err.message : 'Could not book the appointment.');
    }
  }

  const contactOptions = [
    { value: '', label: 'Select a contact…' },
    ...(newContact && !contacts.data?.items.some((c) => c.id === newContact.id)
      ? [newContact]
      : []
    ).map((c) => ({ value: c.id, label: c.displayName })),
    ...(contacts.data?.items ?? []).map((c) => ({ value: c.id, label: c.displayName })),
  ];
  const noServices = services.data && services.data.length === 0;

  return (
    <>
      <Modal title="New appointment" onClose={onClose}>
        {noServices ? (
          <p style={{ margin: 0 }}>
            No service can be booked yet. Give a service a booking schedule on the{' '}
            <Link to="/products-services">Products & Services</Link> page first.
          </p>
        ) : (
          <form className="ne-auth__form" onSubmit={(e) => void onSubmit(e)} noValidate>
            <Select
              label="Contact"
              value={contactId}
              options={contactOptions}
              onChange={(e) => {
                setContactId(e.target.value);
                clear('contactId');
              }}
              error={errors.contactId}
            />
            {hasPermission('contacts.create') ? (
              <div style={{ marginTop: 'calc(-1 * var(--space-2))' }}>
                <Button type="button" variant="secondary" onClick={() => setAddingContact(true)}>
                  + New contact
                </Button>
              </div>
            ) : null}
            <Select
              label="Service"
              value={serviceId}
              options={[
                { value: '', label: services.isPending ? 'Loading…' : 'Select a service…' },
                ...(services.data ?? []).map((s) => ({
                  value: s.id,
                  label: `${s.name} (${s.slotMinutes} min)`,
                })),
              ]}
              onChange={(e) => {
                setServiceId(e.target.value);
                setSlot('');
                clear('productServiceId');
              }}
              error={errors.productServiceId}
            />
            <Select
              label="Time"
              value={slot}
              disabled={!serviceId}
              options={[
                {
                  value: '',
                  label: !serviceId
                    ? 'Choose a service first'
                    : slots.isPending
                      ? 'Loading open times…'
                      : slots.data?.length
                        ? 'Select an open time…'
                        : 'No open times - pick another time',
                },
                ...(slots.data ?? []).map((s) => ({ value: s.startIso, label: s.label })),
                { value: OTHER_TIME, label: 'Another time…' },
              ]}
              onChange={(e) => {
                setSlot(e.target.value);
                clear('scheduledStart');
              }}
              error={slot === OTHER_TIME ? undefined : errors.scheduledStart}
            />
            {slot === OTHER_TIME ? (
              <TextField
                label="Date and time"
                type="datetime-local"
                value={otherTime}
                onChange={(e) => {
                  setOtherTime(e.target.value);
                  clear('scheduledStart');
                }}
                error={errors.scheduledStart}
              />
            ) : null}
            <div className="ne-field">
              <label className="ne-field__label" htmlFor="appointment-notes">
                Notes (optional)
              </label>
              <textarea
                id="appointment-notes"
                className={['ne-input', errors.notes ? 'ne-input--error' : '']
                  .filter(Boolean)
                  .join(' ')}
                rows={3}
                value={notes}
                aria-invalid={errors.notes ? true : undefined}
                onChange={(e) => {
                  setNotes(e.target.value);
                  clear('notes');
                }}
              />
              {errors.notes ? (
                <span className="ne-field__error" role="alert">
                  {errors.notes}
                </span>
              ) : null}
            </div>
            <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
              />
              Send a WhatsApp confirmation (if they have chatted with you)
            </label>
            {error ? (
              <p className="ne-auth__error" role="alert">
                {error}
              </p>
            ) : null}
            <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Booking…' : 'Book appointment'}
              </Button>
            </div>
          </form>
        )}
      </Modal>
      {addingContact ? (
        <QuickContactModal
          onClose={() => setAddingContact(false)}
          onCreated={(c) => {
            setNewContact(c);
            setContactId(c.id);
            clear('contactId');
            setAddingContact(false);
          }}
        />
      ) : null}
    </>
  );
}

/** A Date as the value of an <input type="datetime-local"> (local time). */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
